import { useState } from "react";
import { ImagePlus, Images } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const ProjectCard = ({ index, category, title, photos = [], beforeAfter = false, ratio = "aspect-[4/3]" }) => {
  const [active, setActive] = useState(0);
  const testid = `project-card-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`;

  if (!photos.length) {
    return (
      <motion.figure
        data-testid={testid}
        className={`group relative ${ratio} overflow-hidden rounded-sm border border-dashed border-oak/45 bg-sand transition-colors duration-500 hover:border-oak`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,rgba(200,138,75,0.16),transparent_65%)] transition-opacity duration-500 group-hover:opacity-150" />
        <span className="pointer-events-none absolute -right-3 -top-6 select-none font-serif text-[7rem] font-black leading-none text-walnut/10 transition-colors duration-500 group-hover:text-walnut/20">
          {String(index).padStart(2, "0")}
        </span>
        <figcaption className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-full border border-oak/40 bg-white/70 text-walnut transition-transform duration-500 group-hover:scale-110 group-hover:border-oak">
            <ImagePlus size={22} strokeWidth={1.5} />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-walnut/80">{category}</span>
          <span className="font-serif text-lg font-semibold text-ink/80">{title}</span>
          <span className="text-xs text-clay">Fotografie reală din atelier · în curând</span>
        </figcaption>
        <span className="absolute bottom-0 left-0 h-[3px] w-0 bg-oak transition-[width] duration-500 group-hover:w-full" />
      </motion.figure>
    );
  }

  return (
    <motion.figure data-testid={testid} className="group">
      <div className={`relative ${ratio} overflow-hidden rounded-sm border border-line`}>
        <AnimatePresence mode="wait">
          <motion.img
            key={photos[active]}
            src={photos[active]}
            alt={`${title} · proiect real LOGITEH PROD`}
            loading="lazy"
            initial={{ opacity: 0.3, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,transparent_50%,rgba(24,21,18,0.5)_100%)]" />
        <span className="absolute left-4 top-4 rounded-full bg-anthracite/70 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-oak backdrop-blur-sm">
          Proiect real
        </span>
        {beforeAfter ? (
          <span
            data-testid={`${testid}-state-badge`}
            className="absolute right-4 top-4 rounded-full bg-oak px-3 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.25em] text-anthracite"
          >
            {active === 0 ? "Înainte" : "După"}
          </span>
        ) : (
          photos.length > 1 && (
            <span
              data-testid={`${testid}-photo-count`}
              className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-anthracite/70 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-oak backdrop-blur-sm"
            >
              <Images size={11} /> {photos.length} poze
            </span>
          )
        )}
        <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-anthracite/85 via-anthracite/40 to-transparent p-4 pt-10">
          <span className="font-serif text-lg font-semibold text-cream">{title}</span>
          <span className="text-right font-mono text-[9px] uppercase tracking-[0.25em] text-oak">{category}</span>
        </figcaption>
      </div>
      {photos.length > 1 && beforeAfter && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {["Înainte", "După"].map((lbl, i) => (
            <button
              key={lbl}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`${testid}-${i === 0 ? "before" : "after"}-button`}
              className={`rounded-full py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
                i === active
                  ? "bg-anthracite text-cream"
                  : "border border-line bg-white text-clay hover:border-oak hover:text-walnut"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
      )}
      {photos.length > 1 && !beforeAfter && (
        <div className="mt-3 flex gap-2">
          {photos.map((p, i) => (
            <button
              key={p}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`${testid}-thumb-${i + 1}`}
              aria-label={`Fotografia ${i + 1} din proiectul ${title}`}
              className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-sm border transition-all duration-300 ${
                i === active ? "border-oak ring-1 ring-oak" : "border-line opacity-60 hover:opacity-100"
              }`}
            >
              <img src={p} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.figure>
  );
};
